import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ConfirmDeleteTagsModal from './confirm_delete_tags_modal';
import ConfirmMergeTagsModal from './confirm_merge_tags_modal';
import CreateTagModal from './create_tag_modal';
import DeleteTagsModal from './delete_tags_modal';
import MergeTagsModal from './merge_tags_modal';

import './styles.scss';
import { get } from 'helpers/api';
import TagTopBar from './tag_top_bar';
import TagTable from './tag_table';
import LoadingSpinner from 'components/loading_spinner';

export default function DatabaseTagManager({ databaseId, searchPath }) {
  const [displayDeleteConfirmModal, setDisplayDeleteConfirmModal] = useState(false);
  const [displayDeleteTagsModal, setDisplayDeleteTagsModal] = useState(false);
  const [displayMergeConfirmModal, setDisplayMergeConfirmModal] = useState(false);
  const [displayMergeTagsModal, setDisplayMergeTagsModal] = useState(false);
  const [displayNewTagModal, setDisplayNewTagModal] = useState(false);
  const [mergeTag, setMergeTag] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [searching, setSearching] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const fetchingTimeoutId = useRef();

  async function searchForTags(searchText = searchValue, pageNumber = page) {
    setSearching(true);

    const params = { page: pageNumber, name: searchText };
    const res = await get(searchPath, { params });
    setTags(res.tags);
    setTotalPages(res.totalPages);
    setSearching(false);
  }

  useEffect(() => {
    searchForTags();
  }, []);

  function pageChanged(newPageNumber) {
    setPage(newPageNumber);
    searchForTags(searchValue, newPageNumber);
  }

  const handleTagSearch = (e) => {
    const searchText = e.target.value;

    setSearchValue(searchText);
    setPage(1);
    clearTimeout(fetchingTimeoutId.current);
    fetchingTimeoutId.current = setTimeout(() => searchForTags(searchText, 1), 300);
  };

  const openMergeTagsModal = () => {
    setDisplayMergeTagsModal(selectedTags.length > 1);
  };

  const cancelMergeTags = () => {
    setDisplayMergeConfirmModal(false);
    setDisplayMergeTagsModal(false);
    setMergeTag(null);
    setSelectedTags([]);
  };

  const confirmMergedTags = () => {
    setDisplayMergeConfirmModal(true);
    setDisplayMergeTagsModal(false);
  };

  const openDeleteTagsModal = () => setDisplayDeleteTagsModal(selectedTags.length > 0);

  const cancelDeleteTags = () => {
    setDisplayDeleteTagsModal(false);
    setDisplayDeleteConfirmModal(false);
    setSelectedTags([]);
  };

  const confirmDeleteTags = () => {
    setDisplayDeleteConfirmModal(true);
    setDisplayDeleteTagsModal(false);
  };

  const resetTags = () => {
    setSelectedTags([]);
    searchForTags();
  };

  const selectedTagIds = new Set(selectedTags.map(s => s.id));

  const toggleSelectTag = (tag) => {
    if (selectedTagIds.has(tag.id)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const renderModals = () => {
    return (
      <React.Fragment>
        <CreateTagModal
          currentTags={tags.map(t => t.name)}
          databaseId={databaseId}
          modalDisplay={displayNewTagModal}
          onCloseModal={() => setDisplayNewTagModal(false)}
          onUpdateTags={() => searchForTags()}
        />
        <MergeTagsModal
          modalDisplay={displayMergeTagsModal}
          selectedTag={mergeTag}
          selectedTags={selectedTags}
          onCloseModal={cancelMergeTags}
          onMergeTags={confirmMergedTags}
          onSelect={setMergeTag}
        />
        <ConfirmMergeTagsModal
          databaseId={databaseId}
          mergeTag={mergeTag}
          modalDisplay={displayMergeConfirmModal}
          selectedTags={selectedTags}
          onCloseModal={cancelMergeTags}
          onMergeTags={resetTags}
        />
        <DeleteTagsModal
          modalDisplay={displayDeleteTagsModal}
          selectedTags={selectedTags}
          onCloseModal={cancelDeleteTags}
          onDeleteTags={confirmDeleteTags}
        />
        <ConfirmDeleteTagsModal
          databaseId={databaseId}
          modalDisplay={displayDeleteConfirmModal}
          selectedTags={selectedTags}
          onCloseModal={cancelDeleteTags}
          onDeleteTags={resetTags}
        />
      </React.Fragment>
    );
  };

  return (
    <div>
      <h2 className="h2">
        Tag Management
      </h2>
      <React.Fragment>
        <TagTopBar
          handleTagSearch={handleTagSearch}
          openDeleteTagsModal={openDeleteTagsModal}
          openMergeTagsModal={openMergeTagsModal}
          openNewTagModal={() => setDisplayNewTagModal(true)}
          searchValue={searchValue}
          selectedTagsLength={selectedTags.length}
        />
        {searching ? (
          <LoadingSpinner/>
        ) : (
          <TagTable
            page={page}
            selectedTagIds={selectedTagIds}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            tags={tags}
            toggleSelectTag={toggleSelectTag}
            totalPages={totalPages}
            onPageChange={pageChanged}
          />
        )}
        {renderModals()}
      </React.Fragment>
    </div>
  );
}

DatabaseTagManager.propTypes = {
  databaseId: PropTypes.number.isRequired,
  searchPath: PropTypes.string.isRequired
};
